// Utilidades de teléfono compartidas (llamar / WhatsApp / copiar).
//
// El backend normaliza a E.164 y lo devuelve en `phone_e164`; estas funciones
// son el equivalente en cliente para cuando solo se tiene el texto crudo.

const DEFAULT_COUNTRY_CODE = '52'; // México: mercado principal de la plataforma.

/** Deja solo dígitos y el '+' inicial. */
export const cleanPhone = (raw) => {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  const digits = trimmed.replace(/\D/g, '');
  return trimmed.startsWith('+') ? `+${digits}` : digits;
};

/**
 * E.164 estricto: '+' seguido de un código de país que NUNCA empieza en 0,
 * y entre 8 y 15 dígitos en total. Sirve para validar lo que llega guardado
 * (hay números históricos con prefijo nacional, p. ej. '0412…').
 */
export const isValidE164 = (value) => /^\+[1-9]\d{7,14}$/.test(String(value || ''));

/**
 * Normaliza a E.164 ('+<código><número>').
 * Si no trae código de país, se asume el del mercado por defecto.
 * Devuelve null si no puede formarse un número plausible.
 */
export const toE164 = (raw, countryCode = DEFAULT_COUNTRY_CODE) => {
  const cleaned = cleanPhone(raw);
  if (!cleaned) return null;
  const digits = cleaned.replace(/\D/g, '');
  if (digits.length < 8 || digits.length > 15) return null;
  if (cleaned.startsWith('+')) {
    // Un '+0…' no es E.164 válido (ningún código de país empieza en 0).
    return isValidE164(`+${digits}`) ? `+${digits}` : null;
  }
  // Un número nacional que empieza en 0 lleva prefijo de larga distancia y no
  // permite deducir el país: se marca inválido en vez de arriesgar una llamada
  // a un número equivocado.
  if (digits.startsWith('0')) return null;
  const cc = String(countryCode).replace(/\D/g, '') || DEFAULT_COUNTRY_CODE;
  const candidate = digits.startsWith(cc) && digits.length > 10 ? `+${digits}` : `+${cc}${digits}`;
  return isValidE164(candidate) ? candidate : null;
};

export const isValidPhone = (raw, countryCode) => Boolean(toE164(raw, countryCode));

/** Enlace de llamada. */
export const telLink = (raw, countryCode) => {
  const e164 = toE164(raw, countryCode);
  return e164 ? `tel:${e164}` : null;
};

/** Enlace de WhatsApp (wa.me exige el número SIN '+'). */
export const whatsappLink = (raw, text = '', countryCode) => {
  const e164 = toE164(raw, countryCode);
  if (!e164) return null;
  const base = `https://wa.me/${e164.replace(/\D/g, '')}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
};

/** Formato legible: '+52 818 020 4282'. */
export const formatPhone = (raw, countryCode) => {
  const e164 = toE164(raw, countryCode);
  if (!e164) return raw || '';
  const digits = e164.slice(1);
  const cc = digits.startsWith(DEFAULT_COUNTRY_CODE) ? DEFAULT_COUNTRY_CODE : digits.slice(0, 2);
  const rest = digits.slice(cc.length);
  // Se agrupan los últimos 4 dígitos y el resto de 3 en 3: '+52 818 020 4282'.
  const tail = rest.slice(-4);
  const head = rest.slice(0, -4).replace(/(\d{3})(?=\d)/g, '$1 ').trim();
  return `+${cc} ${[head, tail].filter(Boolean).join(' ')}`;
};
